import React, { createContext, useContext, useState, useEffect, FC, ReactNode } from 'react'

/**
 * Media item interface for modal display
 * Includes all metadata needed for proper download functionality
 */
export interface MediaItem {
  id: string
  type: 'image' | 'video' | 'sticker' | 'image_with_text'
  url: string
  thumbnail?: string
  name?: string
  // Additional metadata for download support:
  filename?: string      // Preferred filename
  fileRecordId?: string  // Fallback filename (FileRecord ID)
  mimeType?: string      // For extension mapping
  extension?: string     // Explicit extension
  duration?: string      // For video/audio (format: "MM:SS")
  size?: number         // File size in bytes
}

/**
 * Internal state for media modal
 */
interface MediaModalState {
  isOpen: boolean
  currentMedia: MediaItem | null
  allMedia: MediaItem[]
  currentIndex: number
}

/**
 * Context value interface
 */
interface MediaModalContextValue {
  isOpen: boolean
  currentMedia: MediaItem | null
  allMedia: MediaItem[]
  currentIndex: number
  openModal: (mediaId: string, allMedia: MediaItem[]) => void
  closeModal: () => void
  navigateNext: () => void
  navigatePrevious: () => void
  navigateToMedia: (mediaId: string) => void
}

/**
 * Create context with undefined default
 */
const MediaModalContext = createContext<MediaModalContextValue | undefined>(undefined)

/**
 * Media Modal Provider Component
 * Manages global state for single shared modal instance
 */
export const MediaModalProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<MediaModalState>({
    isOpen: false,
    currentMedia: null,
    allMedia: [],
    currentIndex: 0
  })

  /**
   * Open modal with specific media item
   * @param mediaId - ID of media to display
   * @param allMedia - All media items in the room for navigation
   */
  const openModal = (mediaId: string, allMedia: MediaItem[]) => {
    const index = allMedia.findIndex(m => m.id === mediaId)
    if (index === -1) {
      console.warn(`[MediaModalContext] Media with id ${mediaId} not found in allMedia array`)
      return
    }

    setState({
      isOpen: true,
      currentMedia: allMedia[index],
      allMedia,
      currentIndex: index
    })
  }

  /**
   * Close modal and reset state
   */
  const closeModal = () => {
    setState(prev => ({ ...prev, isOpen: false }))
  }

  /**
   * Navigate to next media item
   */
  const navigateNext = () => {
    setState(prev => {
      if (prev.currentIndex >= prev.allMedia.length - 1) return prev
      const newIndex = prev.currentIndex + 1
      return {
        ...prev,
        currentIndex: newIndex,
        currentMedia: prev.allMedia[newIndex]
      }
    })
  }

  /**
   * Navigate to previous media item
   */
  const navigatePrevious = () => {
    setState(prev => {
      if (prev.currentIndex <= 0) return prev
      const newIndex = prev.currentIndex - 1
      return {
        ...prev,
        currentIndex: newIndex,
        currentMedia: prev.allMedia[newIndex]
      }
    })
  }

  /**
   * Navigate to specific media by ID
   * @param mediaId - ID of media to navigate to
   */
  const navigateToMedia = (mediaId: string) => {
    setState(prev => {
      const index = prev.allMedia.findIndex(m => m.id === mediaId)
      if (index === -1) {
        console.warn(`[MediaModalContext] Media with id ${mediaId} not found for navigation`)
        return prev
      }
      return {
        ...prev,
        currentIndex: index,
        currentMedia: prev.allMedia[index]
      }
    })
  }

  /**
   * Keyboard navigation handler
   * Supports: ESC (close), Arrow Left (previous), Arrow Right (next)
   */
  useEffect(() => {
    if (!state.isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          closeModal()
          break
        case 'ArrowLeft':
          navigatePrevious()
          break
        case 'ArrowRight':
          navigateNext()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [state.isOpen, state.currentIndex, state.allMedia.length])

  /**
   * Body scroll lock when modal is open
   * Prevents background scrolling
   */
  useEffect(() => {
    if (state.isOpen) {
      document.body.style.overflow = 'hidden'
      document.body.style.paddingRight = '0px'
    } else {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    }

    return () => {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    }
  }, [state.isOpen])

  return (
    <MediaModalContext.Provider
      value={{
        ...state,
        openModal,
        closeModal,
        navigateNext,
        navigatePrevious,
        navigateToMedia
      }}
    >
      {children}
    </MediaModalContext.Provider>
  )
}

/**
 * Custom hook to access media modal context
 * @throws Error if used outside of MediaModalProvider
 */
export const useMediaModal = () => {
  const context = useContext(MediaModalContext)
  if (!context) {
    throw new Error('useMediaModal must be used within MediaModalProvider')
  }
  return context
}
